import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { LogoMark } from '../../logo/logo-mark';
import { publicApi } from '../../../api/public-client';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { AutoHeight, StateSwitch } from '../../../motion';
import { PublicPass } from '../../../interfaces/visitor-pass';
import {
  describeSchedule,
  describeVisit,
  formatPassDate,
  zoneNote,
} from '../../../utils/pass-schedule';
import { palette, spacing, typography } from '../../../style-utils/theme';
import { Qr } from '../invite/invite-qr-modal';
import {
  BrandRow,
  Card,
  FieldSkeleton,
  FullWidthButton,
  HeadingSkeleton,
  Heading,
  HelperText,
  Shell,
  SkeletonGroup,
  Subheading,
  Wordmark,
} from '../onboarding.styled';

/**
 * THE GUEST'S PASS PAGE — /p/:token (resident-portal Plan 6, RP-6.3).
 *
 * A resident shared their visitor's pass as a link; the visitor opens it on
 * their phone, with no account and no session. The page says who is expected
 * where and when, and shows the code the front desk scans. It never shows the
 * flat number (RP-D8 — the code carries the pass for the desk), a note, a
 * surname, or anything else of the household's.
 *
 * ONE answer for a link that does not work, whatever the reason (stopped,
 * ended, not yet approved, mistyped): the server says 404 and so does the page.
 *
 * OPENED TOO OFTEN (RP-9.7). The endpoint allows 30 reads a minute per address.
 * A guest who hits that used to be told to check their connection and handed a
 * "Try again" button — each press another refused read. Now the page says what
 * happened and offers nothing to press until the minute has passed.
 *
 * NO REFERRER: the URL is the credential, so the page asks the browser to send
 * no Referer from it (a <meta> while it is open — the global header stays
 * strict-origin-when-cross-origin because the Maps key needs the origin; see
 * deploy/ops/Caddyfile), and the API answers no-store + no-referrer as well.
 *
 * ORGANISATION SUSPENDED (Plan 3 F1). The desk refuses every request then, so
 * the page shows no code — the paused page's shape with its own sentence. The
 * server sends no QR in that case; the page comes back by itself on restore.
 *
 * Public route (PUBLIC_PREFIXES) AND rendered for a signed-in visitor too
 * (SIGNED_IN_PUBLIC_PREFIXES): a resident checking the link they just shared
 * must see it, and it touches nobody's session.
 */

type View = 'loading' | 'ready' | 'invalid' | 'error' | 'limited';

export const LIMITED_TITLE = 'This page was opened too many times';
export const LIMITED_TEXT = 'Wait a minute and open the link again.';
/** How long the rate-limited page offers no retry: the limiter's own window. */
export const LIMITED_WAIT_MS = 60_000;

const Facts = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: 0 0 ${spacing.md};
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  line-height: ${typography.leading.normal};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const Fact = styled.p`
  margin: 0;
`;

const Quiet = styled.p`
  margin: 0;
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

const CodeBlock = styled.figure`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.xs};
  margin: 0;
`;

const CodeCaption = styled.figcaption`
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  text-align: center;
`;

/** Ask the browser to send no Referer from this page while it is open. */
const useNoReferrer = (): void => {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);

    return () => {
      meta.remove();
    };
  }, []);
};

export const PAUSED_NO_CODE =
  'Your code will be shown here again when the pass is resumed. This link stays the same.';

export const SUSPENDED_LINE = 'The front desk cannot sign visitors in at the moment.';
export const SUSPENDED_NO_CODE =
  'Your code will be shown here again when the front desk can sign you in. This link stays the same.';

export const pausedLine = (pass: PublicPass): string => {
  const { status, paused_until: until } = pass.schedule;
  if (status !== 'paused') return '';
  if (until && until >= pass.today) return `This pass is paused until ${formatPassDate(until)}.`;
  if (!until) return 'This pass is paused for now.';

  return '';
};

export const PublicPassPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  useNoReferrer();
  const [view, setView] = useState<View>('loading');
  const [pass, setPass] = useState<PublicPass | null>(null);
  // Rate-limited: no retry is offered until the limiter's minute has passed.
  const [canRetry, setCanRetry] = useState(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);

  useEffect(
    () => () => {
      if (retryTimer.current) clearTimeout(retryTimer.current);
    },
    []
  );

  const load = useCallback(() => {
    const mine = ++seq.current;
    setView('loading');
    publicApi
      .get(`/pass-links/${encodeURIComponent(token)}`)
      .then(res => {
        if (mine !== seq.current) return;
        const data = res.data as PublicPass;
        if (!data || !data.guest_first_name || (!data.qr_data_url && !data.suspended)) {
          setView('invalid');

          return;
        }
        setPass(data);
        setView('ready');
      })
      .catch(err => {
        if (mine !== seq.current) return;
        const status = err?.response?.status;
        if (status === 429) {
          setCanRetry(false);
          if (retryTimer.current) clearTimeout(retryTimer.current);
          retryTimer.current = setTimeout(() => {
            retryTimer.current = null;
            setCanRetry(true);
          }, LIMITED_WAIT_MS);
          setView('limited');

          return;
        }
        setView(status === 404 ? 'invalid' : 'error');
      });
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useDocumentTitle(view === 'ready' && pass ? `Visitor pass for ${pass.guest_first_name}` : 'Visitor pass');

  const zone = pass?.building.timezone || 'UTC';
  const suspended = !!pass?.suspended;
  const paused = pass ? pausedLine(pass) : '';
  const note = pass ? zoneNote(zone) : '';

  return (
    <Shell>
      <Card>
        <BrandRow>
          <LogoMark size={40} aria-hidden="true" />
          <Wordmark>Lobbero</Wordmark>
        </BrandRow>

        <AutoHeight>
          <StateSwitch state={view}>
            {view === 'loading' && (
              <SkeletonGroup label="Loading the visitor pass">
                <HeadingSkeleton />
                <FieldSkeleton />
                <FieldSkeleton />
              </SkeletonGroup>
            )}

            {view === 'invalid' && (
              <>
                <Heading>This pass link is not valid</Heading>
                <Subheading>
                  It may have been stopped, or the visits it was for have ended. Ask the person you are visiting to
                  send you a new link.
                </Subheading>
              </>
            )}

            {view === 'error' && (
              <>
                <Heading>We could not open this pass</Heading>
                <Subheading>Check your connection and try again.</Subheading>
                <FullWidthButton type="button" onClick={load}>
                  Try again
                </FullWidthButton>
              </>
            )}

            {view === 'limited' && (
              <>
                <Heading>{LIMITED_TITLE}</Heading>
                <Subheading>{LIMITED_TEXT}</Subheading>
                {canRetry ? (
                  <FullWidthButton type="button" onClick={load}>
                    Try again
                  </FullWidthButton>
                ) : null}
              </>
            )}

            {view === 'ready' && pass && (
              <>
                <Heading>{`Hi ${pass.guest_first_name} — you’re expected at ${pass.building.name}`}</Heading>
                <Subheading>
                  {pass.host_first_name
                    ? `${pass.host_first_name} has added you as a visitor.`
                    : 'You have been added as a visitor.'}
                </Subheading>
                <Facts>
                  <Fact>{describeSchedule(pass.schedule, { today: pass.today })}</Fact>
                  {suspended ? (
                    <Fact>{SUSPENDED_LINE}</Fact>
                  ) : paused ? (
                    <Fact>{paused}</Fact>
                  ) : pass.next ? (
                    <Fact>{`Next visit: ${describeVisit(pass.next, zone, pass.today)}`}</Fact>
                  ) : (
                    <Fact>No visits are coming up on this pass.</Fact>
                  )}
                  {pass.building.address ? <Quiet>{pass.building.address}</Quiet> : null}
                  {note ? <Quiet>{note}</Quiet> : null}
                </Facts>
                {/* A paused pass is not expected at the desk, so it shows no
                    code to hand over; the same link brings it back when the
                    pass is resumed. */}
                {suspended ? (
                  <HelperText>{SUSPENDED_NO_CODE}</HelperText>
                ) : paused ? (
                  <HelperText>{PAUSED_NO_CODE}</HelperText>
                ) : (
                  <>
                    <CodeBlock>
                      <Qr src={pass.qr_data_url ?? ''} alt="Pass code for the front desk" />
                      <CodeCaption>Show this at the front desk</CodeCaption>
                    </CodeBlock>
                    <HelperText>
                      The front desk scans this code and checks who you are. It is not a key and does not open any door.
                    </HelperText>
                  </>
                )}
              </>
            )}
          </StateSwitch>
        </AutoHeight>
      </Card>
    </Shell>
  );
};
